import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import type { HelpdeskTaskSearchQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-search-query.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';

@Component({
  selector: 'crm-hd-task-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, StatusLabelComponent],
  templateUrl: './hd-task-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HdTaskMultiSelectComponent implements OnInit, OnChanges {
  private readonly LIMIT = 20;

  @Input() public tasks: HelpdeskTaskFindInterface[] = [];

  @Input() public selectedTasks: HelpdeskTaskFindInterface[] = [];
  @Output() public selectedTasksChange = new EventEmitter<HelpdeskTaskFindInterface[]>();

  @Input() public isCustomSearch = true;
  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;

  private lazyLoading = false;
  private maxCount = 0;
  private offset = 0;
  private search = '';

  private get getQueryParams(): HelpdeskTaskSearchQueryInterface {
    const queryParams: HelpdeskTaskSearchQueryInterface = {
      limit: this.LIMIT,
      offset: this.offset
    };
    if (this.search?.length) {
      queryParams.search = this.search;
    }
    return queryParams;
  }

  public constructor(
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.isCustomSearch) {
      this.firstLoadTasks();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedTasks?.currentValue) {
      this.selectedTasks.forEach((task) => {
        task['name'] = 'Заявка №' + task.id;
      });
      if (!this.isCustomSearch) {
        this.tasks.unshift(...this.selectedTasks);
      }
    }
    if (changes.tasks?.currentValue) {
      this.tasks.forEach((task) => {
        task['name'] = 'Заявка №' + task.id;
      });
    }
  }

  public searchTasks(event: { first: number; last: number }) {
    if (this.offset <= event.last && this.maxCount > this.tasks?.length && !this.lazyLoading && !this.isCustomSearch) {
      this.lazyLoading = true;
      this.helpDeskTaskService.search(this.getQueryParams).subscribe({
        next: (res) => {
          res.items.forEach((task) => {
            task['name'] = 'Заявка №' + task.id;
          });
          this.offset += this.LIMIT;
          this.tasks = this.tasks.concat(res.items);
          this.cdRef.markForCheck();
        },
        complete: () => {
          this.lazyLoading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public filterTasks(event: { originalEvent: InputEvent; filter: string }) {
    if (!this.isCustomSearch) {
      this.search = event.filter;
      this.offset = 0;
      if (this.search?.length) {
        this.lazyLoading = true;
        this.helpDeskTaskService.search(this.getQueryParams).subscribe({
          next: (res) => {
            res.items.forEach((task) => {
              task['name'] = 'Заявка №' + task.id;
            });
            this.tasks = [...this.selectedTasks, ...res.items];
            this.maxCount = res.count;
            this.offset = this.LIMIT;
            this.cdRef.markForCheck();
          },
          complete: () => {
            this.lazyLoading = false;
            this.cdRef.markForCheck();
          }
        });
      } else {
        this.firstLoadTasks();
      }
    }
  }

  public toTask(task: any): HelpdeskTaskFindInterface {
    return task as HelpdeskTaskFindInterface;
  }

  private firstLoadTasks() {
    this.helpDeskTaskService.search(this.getQueryParams).subscribe({
      next: (res) => {
        res.items.forEach((task) => {
          task['name'] = 'Заявка №' + task.id;
        });
        this.tasks = this.tasks.concat(res.items);
        this.maxCount = res.count;
        this.offset = this.LIMIT;
        this.cdRef.markForCheck();
      }
    });
  }
}
